import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../firebase/config";
import { User, signOut } from "firebase/auth";

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  isGuest: boolean;
  logout: () => Promise<void>;
  enterGuestMode: () => void;
  exitGuestMode: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const GUEST_SESSION_KEY = "council-guest-session";
function readGuestSession() {
  try { return sessionStorage.getItem(GUEST_SESSION_KEY) === "true"; } catch { return false; }
}
function storeGuestSession(guest: boolean) {
  try { if (guest) sessionStorage.setItem(GUEST_SESSION_KEY, "true"); else sessionStorage.removeItem(GUEST_SESSION_KEY); } catch { /* Private browsing can disable session storage. */ }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(readGuestSession);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      if (user) {
        setIsGuest(false); // signed-in user is never a guest
        storeGuestSession(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const enterGuestMode = () => {
    setIsGuest(true);
    storeGuestSession(true);
  };

  const exitGuestMode = () => {
    setIsGuest(false);
    storeGuestSession(false);
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setIsGuest(false);
      storeGuestSession(false);
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  const value = {
    currentUser,
    loading,
    isGuest,
    logout,
    enterGuestMode,
    exitGuestMode,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <div className="auth-loading" role="status"><span className="auth-loading-dot" /><p>Loading…</p></div> : children}
    </AuthContext.Provider>
  );
};
